import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useConfig, PaymentToken } from './ConfigContext';

// ============================================================================
// Frontend types
// ============================================================================

export interface PromoPlan {
  id: number;
  name: string;
  slug: string;
  description: string;
  priceUsd: number;
  isFeatured: boolean;
  isActive: boolean;
}

export interface PromoToken {
  ticker: string;
  name: string;
  logoUrl: string | null;
}

export interface PromoAvailability {
  totalSlots: number;
  usedSlots: number;
  availableSlots: number;
  featuredSlots: number;
  usedFeaturedSlots: number;
  availableFeaturedSlots: number;
}

export interface PromoConfig {
  plans: PromoPlan[];
  paymentTokens: PaymentToken[];
  receiverWallet: string;
  discounts: Record<number, number>;
  availability: PromoAvailability;
  maxTokensPerWallet: number;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

// ============================================================================
// API-shape types (Session 1, PT-P1-5 pragmatic fix)
//
// Mirror dexscan-api's `/promo/plans` response. Critically `availability`
// and `featured_availability` are TWO SEPARATE objects with overlapping
// field names — PT-P1-1 was caused by the portal reading featured-slot
// counts from `availability.featured_slots` (which doesn't exist) instead
// of from `featured_availability.total_slots` etc.
// ============================================================================

interface ApiPromoPlanRow {
  id: number;
  name: string;
  slug: string;
  description?: string;
  price_usd: number | string;
  is_featured?: boolean;
  is_active?: boolean;
}

interface ApiPromoDiscountRow {
  months?: number;
  discount_percent?: number;
}

interface ApiPromoSlotInfo {
  total_slots: number;
  used_slots: number;
  available_slots: number;
}

interface ApiPromoPlansEnvelope {
  code: string;
  data?: {
    plans?: ApiPromoPlanRow[];
    discounts?: ApiPromoDiscountRow[];
    availability?: ApiPromoSlotInfo;
    /** PT-P1-1: featured-slot counts live HERE, not inside `availability`. */
    featured_availability?: ApiPromoSlotInfo;
    receiver_wallet?: string;
    max_tokens_per_wallet?: number;
  };
}

// ============================================================================
// Initial state
// ============================================================================

const defaultConfig: PromoConfig = {
  plans: [],
  paymentTokens: [],
  receiverWallet: '',
  discounts: {},
  availability: {
    totalSlots: 6,
    usedSlots: 0,
    availableSlots: 6,
    featuredSlots: 2,
    usedFeaturedSlots: 0,
    availableFeaturedSlots: 2,
  },
  maxTokensPerWallet: 2,
  isLoading: true,
  error: null,
  refresh: () => {},
};

const PromoContext = createContext<PromoConfig>(defaultConfig);

interface PromoProviderProps {
  children: ReactNode;
}

export function PromoProvider({ children }: PromoProviderProps) {
  const { apiUrl, paymentTokens, receiverWallet, isLoading: configLoading } = useConfig();
  const [config, setConfig] = useState<PromoConfig>(defaultConfig);

  const fetchConfig = useCallback(async () => {
    try {
      const plansResponse = await fetch(`${apiUrl}/promo/plans`);
      if (!plansResponse.ok) {
        throw new Error(`Failed to fetch promo plans (HTTP ${plansResponse.status})`);
      }
      const plansData = (await plansResponse.json()) as ApiPromoPlansEnvelope;

      if (plansData.code !== 'ok' || !plansData.data) {
        throw new Error('Invalid promo plans response shape');
      }

      // Transform plans
      const plans: PromoPlan[] = (plansData.data.plans || []).map((p) => ({
        id: p.id,
        name: p.name,
        slug: p.slug,
        description: p.description || '',
        priceUsd: typeof p.price_usd === 'number' ? p.price_usd : parseFloat(p.price_usd) || 0,
        isFeatured: p.is_featured || false,
        isActive: p.is_active || false,
      }));

      // Transform discounts
      const discounts: Record<number, number> = {};
      (plansData.data.discounts || []).forEach((d) => {
        if (d.months !== undefined && d.discount_percent !== undefined) {
          discounts[d.months] = d.discount_percent / 100;
        }
      });

      // Session 1 (PT-P1-1, 2026-05-25): featured-slot counts live in the
      // `featured_availability` sibling object, NOT inside `availability`.
      // The previous reads of `availability?.featured_slots` etc. always
      // returned undefined → defaults `2 / 0 / 2` rendered → the "Sold Out"
      // overlay on the featured plan card (PromoPricing.tsx:236-238) is
      // wired to `!hasFeaturedSlots = (availableFeaturedSlots > 0)`, which
      // was always true → overlay never rendered. The Featured Slots
      // availability card always showed "2 / 2".
      const generalAvail = plansData.data.availability;
      const featuredAvail = plansData.data.featured_availability;

      const availability: PromoAvailability = {
        totalSlots: generalAvail?.total_slots ?? 6,
        usedSlots: generalAvail?.used_slots ?? 0,
        availableSlots: generalAvail?.available_slots ?? 6,
        featuredSlots: featuredAvail?.total_slots ?? 2,
        usedFeaturedSlots: featuredAvail?.used_slots ?? 0,
        availableFeaturedSlots: featuredAvail?.available_slots ?? 2,
      };

      // Session 1 (PT-P1-3): if /promo/plans returns no receiver_wallet,
      // fall back to the one ConfigContext loaded (which is itself
      // fail-closed). If neither has one, throw — refuse to render checkout.
      const promoReceiver = plansData.data.receiver_wallet || receiverWallet;
      if (!promoReceiver) {
        throw new Error('No receiver_wallet available from /promo/plans or /subscription/plans');
      }

      setConfig((prev) => ({
        ...prev,
        plans,
        paymentTokens,
        receiverWallet: promoReceiver,
        discounts,
        availability,
        maxTokensPerWallet: plansData.data?.max_tokens_per_wallet ?? 2,
        isLoading: false,
        error: null,
      }));
    } catch (err) {
      console.error('Failed to load promo config:', err);
      setConfig((prev) => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to load promo config',
      }));
    }
  }, [apiUrl, paymentTokens, receiverWallet]);

  // Session 1 (PT-P2-13, 2026-05-25): gate behind ConfigContext's loaded
  // state. The previous effect fired immediately on mount with default
  // paymentTokens=[], then re-fired when ConfigContext loaded — two
  // redundant fetches per session. The `!configLoading` guard collapses
  // them to one.
  useEffect(() => {
    if (apiUrl && !configLoading) {
      fetchConfig();
    }
  }, [apiUrl, configLoading, fetchConfig]);

  const contextValue: PromoConfig = {
    ...config,
    refresh: fetchConfig,
  };

  return (
    <PromoContext.Provider value={contextValue}>
      {children}
    </PromoContext.Provider>
  );
}

export function usePromoConfig() {
  const context = useContext(PromoContext);
  if (!context) {
    throw new Error('usePromoConfig must be used within PromoProvider');
  }
  return context;
}
